import { BusinessInterest } from "../../enums";
import { Program } from "../../services/api";

type ShopifyProgramMetadata = Pick<Program, "shopifyCustomerAccountsVersion" | "shopifyInstalledChannels"> | null;

const ONLINE_CHANNELS = ["web", "shop", "online-store"];
const POS_CHANNELS = ["pos", "retail", "point-of-sale"];
const REQUIRED_ONLINE_STEP_KEYS = ["loyalty-hub", "earning-flows", "rewards"];
const PHYSICAL_STEP_KEYS = ["store-qr", "pos-tiles", "receipt-qr", "loyalty-card"];
const PHYSICAL_CUSTOMER_WEBAPP_STEP_KEY = "widget-webapp";

export const isNewCustomerAccounts = (program: ShopifyProgramMetadata): boolean =>
  program?.shopifyCustomerAccountsVersion === Program.ShopifyCustomerAccountsVersionEnum.NewCustomerAccounts;

export const getShopifyInstalledChannels = (program: ShopifyProgramMetadata): string[] =>
  program?.shopifyInstalledChannels?.map(channel => channel.toLowerCase()) ?? [];

export const hasUnknownShopifyChannels = (program: ShopifyProgramMetadata): boolean =>
  !program?.shopifyInstalledChannels?.length;

export const hasOnlineShopifyChannel = (program: ShopifyProgramMetadata): boolean => {
  const channels = getShopifyInstalledChannels(program);
  return channels.length === 0 || channels.some(channel => ONLINE_CHANNELS.includes(channel));
};

export const hasPosShopifyChannel = (program: ShopifyProgramMetadata): boolean =>
  getShopifyInstalledChannels(program).some(channel => POS_CHANNELS.includes(channel));

export const shouldShowPhysicalShopifySetup = (program: ShopifyProgramMetadata): boolean =>
  hasUnknownShopifyChannels(program) || hasPosShopifyChannel(program);

export const shouldShowPhysicalCustomerWebappHomeStep = (program: ShopifyProgramMetadata): boolean =>
  shouldShowPhysicalShopifySetup(program) && !hasOnlineShopifyChannel(program);

export const getRequiredOnlineHomeStepKeys = (program: ShopifyProgramMetadata): string[] => {
  if (!hasOnlineShopifyChannel(program)) return [];

  return [...REQUIRED_ONLINE_STEP_KEYS];
};

export const getRequiredPhysicalHomeStepKeys = (program: ShopifyProgramMetadata): string[] => {
  if (!shouldShowPhysicalShopifySetup(program)) return [];

  return shouldShowPhysicalCustomerWebappHomeStep(program)
    ? [...PHYSICAL_STEP_KEYS, PHYSICAL_CUSTOMER_WEBAPP_STEP_KEY]
    : [...PHYSICAL_STEP_KEYS];
};

export const getRequiredHomeStepKeys = (program: ShopifyProgramMetadata, isShopify: boolean): string[] => {
  const onlineStepKeys = getRequiredOnlineHomeStepKeys(program);
  return isShopify ? [...onlineStepKeys, ...getRequiredPhysicalHomeStepKeys(program)] : onlineStepKeys;
};

export const getShopifyBusinessInterests = (program: ShopifyProgramMetadata): BusinessInterest[] => {
  const interests: BusinessInterest[] = [];

  if (hasOnlineShopifyChannel(program)) {
    interests.push(BusinessInterest.OnlineLoyalty);
  }

  if (hasPosShopifyChannel(program)) {
    interests.push(BusinessInterest.PhysicalStores);
  }

  return interests.length ? interests : [BusinessInterest.OnlineLoyalty];
};
